/**
 * Ledger amounts travel as integer paisa. These are the only conversions;
 * screens never divide or multiply by 100 themselves.
 */

/** Rupees as typed (e.g. "1,250.5") → paisa, or null if not a valid amount. */
export const toPaisa = (rupees: string | number): number | null => {
  const n = typeof rupees === 'number' ? rupees : Number(String(rupees).replace(/,/g, '').trim());
  if (!Number.isFinite(n) || n < 0) return null;
  if (Math.abs(n * 100 - Math.round(n * 100)) > 1e-6) return null;
  return Math.round(n * 100);
};

/** Like `toPaisa`, but a leading minus is allowed (an overdrawn bank, a debit on a liability). Empty is null. */
export const toSignedPaisa = (rupees: string): number | null => {
  const t = rupees.trim();
  if (!t) return null;
  const paisa = toPaisa(t.startsWith('-') ? t.slice(1) : t);
  return paisa === null ? null : t.startsWith('-') ? -paisa : paisa;
};

/** A debit-positive balance as accountants write it: "1,250.00 Dr", "300.00 Cr", "0.00". */
export const formatDrCr = (paisa: number, currency?: string): string =>
  (paisa === 0 ? formatPaisa(0, currency) : `${formatPaisa(Math.abs(paisa), currency)} ${paisa > 0 ? 'Dr' : 'Cr'}`);

/** Paisa → a statement figure: negatives in brackets, e.g. "(1,250.50)". */
export const formatStatementPaisa = (paisa: number, currency?: string): string =>
  (paisa < 0 ? `(${formatPaisa(-paisa, currency)})` : formatPaisa(paisa, currency));

/** Paisa → an accounting figure with two decimals, e.g. "1,250.50" — or "PKR 1,250.50" given the currency. */
export const formatPaisa = (paisa: number, currency?: string): string => {
  const figure = (paisa / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${figure}` : figure;
};
